<template>
  <div class="qr-generator">
    <h2>직원 명부 QR 코드 생성기</h2>

    아래와 같은 양식으로 엑셀 파일을 업로드 해주십시요.
    <div class="tables-wrapper">
      <table class="table">
        <th>name</th>
        <th>rank</th>
        <th>phone</th>
        <th>email</th>
        <th>notes</th>
        <tr>
          <td>서문환</td>
          <td>과장</td>
          <td>34005352</td>
          <td>zanmoo@naver.com</td>
          <td>고기러버</td>
        </tr>
      </table>
    </div>
    <br />
    <!-- 엑셀 파일 업로드 입력란 -->
    <input type="file" @change="handleFileUpload" accept=".xlsx, .xls" />

    <div v-if="peopleList.length > 0">
      <button @click="downloadExcel">엑셀 다운로드</button>
      <button @click="downloadAllQRCodes">QR 코드 ZIP 다운로드</button>
    </div>

    <!-- 직원 리스트 출력 -->
    <div v-if="peopleList.length > 0" class="people-list">
      <div
        v-for="(person, index) in peopleList"
        :key="index"
        class="person-item"
      >
        <h3>ID: {{ person.uid }}</h3>
        <h3>{{ person.name }} ({{ person.rank }})</h3>
        <p>📞 {{ person.phone }}</p>
        <p>📧 {{ person.email }}</p>
        <p>📝 {{ person.notes }}</p>
        <div v-if="person.qrCode">
          <img :src="person.qrCode" alt="Generated QR Code" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from "qrcode";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export default {
  data() {
    return {
      peopleList: [], // 직원 명부 배열
    };
  },
  methods: {
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]]; // 첫 번째 시트 사용
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        // 엑셀 데이터를 QR 코드로 변환 및 배열에 추가
        this.peopleList = await Promise.all(
          jsonData.map(async (row) => {
            const uid = this.generateUID(); // 랜덤 8자리 UID 생성
            const qrCode = await QRCode.toDataURL(
              `${uid} - ${row.name} - ${row.phone}`
            );
            return {
              uid,
              name: row.name || "N/A",
              phone: row.phone || "N/A",
              rank: row.rank || "N/A",
              email: row.email || "N/A",
              notes: row.notes || "N/A",
              qrCode,
            };
          })
        );
      } catch (err) {
        console.error("엑셀 파일 처리 실패:", err);
      }
    },

    generateUID() {
      return Math.random().toString(36).substr(2, 8).toUpperCase();
    },

    downloadExcel() {
      const worksheet = XLSX.utils.json_to_sheet(this.peopleList);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "직원 명부");
      XLSX.writeFile(workbook, "직원명부_with_QR.xlsx");
    },

    async downloadAllQRCodes() {
      const zip = new JSZip();

      for (let person of this.peopleList) {
        const fileName = `${person.uid}-${person.name}-${person.phone}.png`;
        const base64Data = person.qrCode.replace(
          /^data:image\/png;base64,/,
          ""
        );
        zip.file(fileName, base64Data, { base64: true });
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "QRCode_Pack.zip");
    },
  },
};
</script>

<style scoped>
@media (max-width: 768px) {
  .table th,
  .table td {
    padding: 8px;
    font-size: 10px;
  }

  .table {
    font-size: 12px;
  }
}

.qr-generator {
  max-width: 960px;
  margin: 40px auto;
  padding: 20px;
  background-color: #fff;
  text-align: center;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.qr-generator h2 {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
  font-weight: 700;
}

.table {
  display: inline-block; /* 중앙 정렬 효과 */
  margin: 0 auto; /* 좌우 중앙 */
  white-space: nowrap;
  border-collapse: collapse;
  font-size: 14px;
}
.table-wrapper {
  width: 100%;
  overflow-x: auto;
  text-align: center;
}

.table th,
.table td {
  border: 1px solid #ddd;
  padding: 12px;
}

.table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.table tr:nth-child(even) {
  background-color: #fafafa;
}

input[type="file"] {
  margin: 20px 0;
}

.people-list {
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.person-item {
  background-color: #fefefe;
  border: 1px solid #ddd;
  padding: 16px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  text-align: left;
  transition: transform 0.2s ease;
}

.person-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.person-item h3 {
  margin: 6px 0;
  color: #333;
}

.person-item p {
  margin: 4px 0;
  font-size: 14px;
  color: #555;
}

img {
  margin-top: 10px;
  max-width: 100%;
  border-radius: 4px;
}

button {
  margin: 10px 5px;
  padding: 10px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  font-size: 14px;
  font-weight: bold;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: #0056b3;
}
</style>

<!-- 
<div class="table-wrapper">   이게 중심을 잡아줌     

  <table class="table">      이건 inline-block으로 중앙 정렬 
</table></div>
.table-wrapper {
  overflow-x: auto;       /* 가로 스크롤 허용 */
  text-align: center;     /* 안쪽 콘텐츠를 중앙 정렬 */
}

.table {
  display: inline-block;  /* inline처럼 취급 → 중앙 정렬 가능 */
  margin: 0 auto;         /* 상하 0, 좌우 자동 → 가로 가운데 정렬 */
}

.table-wrapper  text-align:center; 자식요소가 inline-block이면 이 속성으로 중앙 정렬됨
.table          display: inline-block;  block처럼 줄 쓰되, inline처럼 정렬 기준 잡히게 됨
.table          margin: 0 auto;   inline-block 이기 때문에 중앙에서 정렬가능    


-->
